if (!customElements.get('photo-charm')) {
  customElements.define('photo-charm', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-charm-config]').textContent);
      this.messages = this.config.messages;
      this.form = this.querySelector('[data-photo-charm-form]');
      this.photoInput = this.querySelector('[data-charm-photo]');
      this.textInput = this.querySelector('[data-charm-text]');
      this.submit = this.querySelector('[data-charm-submit]');
      this.review = this.querySelector('[data-charm-review]');
      this.scene = this.querySelector('[data-charm-scene]');
      this.detail = this.querySelector('[data-charm-detail]');
      this.error = this.querySelector('[data-charm-error]');
      this.status = this.querySelector('[data-charm-save-status]');
      this.crop = { zoom: 1, horizontal: 0, vertical: 0, rotation: 0 };
      this.photoRevision = 0;
      this.ready = false;
      this.busy = false;
      this.form.addEventListener('submit', event => this.add(event));
      this.addEventListener('change', event => {
        if (event.target === this.photoInput) this.loadPhoto();
        if (event.target.matches('[data-charm-variant]')) this.updateVariant(true);
        if (event.target.matches('[data-charm-quote]')) this.changed();
      });
      this.addEventListener('input', event => {
        if (event.target === this.textInput) {
          this.textInput.setCustomValidity(this.textInput.value.trim() ? '' : this.messages.text_error);
          this.changed();
        }
        if (event.target.matches('[data-charm-control]')) {
          this.crop[event.target.dataset.charmControl] = Number(event.target.value);
          this.changed();
        }
      });
      this.addEventListener('click', event => {
        const gallery = event.target.closest('[data-charm-gallery]');
        if (gallery) {
          const image = this.querySelector('.photo-charm-gallery-image');
          image.removeAttribute('srcset'); image.src = gallery.dataset.charmGallery; image.alt = gallery.dataset.alt;
          this.showPreview(false);
          this.querySelectorAll('[data-charm-gallery]').forEach(button => button.setAttribute('aria-pressed', String(button === gallery)));
        }
        if (event.target.closest('[data-charm-show-preview]')) this.showPreview(true);
        if (event.target.closest('[data-charm-rotate]')) {
          this.crop.rotation = (this.crop.rotation + 90) % 360;
          this.preparePhoto(); this.changed();
        }
        if (event.target.closest('[data-charm-reset]')) {
          this.resetCrop(); this.preparePhoto(); this.changed();
        }
      });
      this.status.textContent = this.messages.loading;
      this.loadAssets();
    }

    async loadAssets() {
      try {
        this.assets = Object.fromEntries(await Promise.all(Object.entries(this.config.assets).map(async ([key, url]) => {
          const img = new Image(); img.crossOrigin = 'anonymous'; img.src = url; await img.decode(); return [key, img];
        })));
        await document.fonts.load('600 35px "Echoes Serif"');
        this.ready = true;
        this.status.textContent = '';
        this.updateVariant(false);
        this.render();
        this.dataset.ready = 'true';
      } catch {
        this.showError(this.messages.load_error);
        this.status.textContent = '';
      }
    }

    updateVariant(updateUrl) {
      const selected = this.querySelector('[data-charm-variant]:checked');
      this.available = selected?.dataset.available === 'true';
      if (selected) this.querySelector('[data-charm-price]').textContent = selected.dataset.price;
      this.submit.disabled = !this.ready || !this.available || this.busy || this.loadingPhoto;
      this.querySelector('[data-charm-submit-label]').textContent = this.busy ? this.messages.saving : (this.available ? this.messages.add : this.messages.sold_out);
      if (updateUrl && selected) {
        const url = new URL(location.href); url.searchParams.set('variant', selected.value); history.replaceState({}, '', url);
      }
    }

    resetCrop() {
      this.crop = { zoom: 1, horizontal: 0, vertical: 0, rotation: 0 };
      this.querySelectorAll('[data-charm-control]').forEach(input => input.value = this.crop[input.dataset.charmControl]);
    }

    async loadPhoto() {
      const revision = ++this.photoRevision;
      const file = this.photoInput.files[0];
      this.loadingPhoto = false; this.updateVariant(false);
      this.photo = null; this.rotatedPhoto = null; this.review.checked = false;
      this.querySelector('[data-charm-editor]').hidden = true;
      this.querySelector('[data-charm-show-preview]').hidden = true;
      this.querySelector('[data-charm-photo-status]').textContent = '';
      this.showPreview(false); this.clearError();
      this.photoInput.setCustomValidity('');
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        this.photoInput.value = ''; this.showError(this.messages.file_error); return;
      }
      this.loadingPhoto = true; this.updateVariant(false);
      const url = URL.createObjectURL(file);
      try {
        const photo = new Image(); photo.src = url; await photo.decode();
        if (revision !== this.photoRevision) return;
        if (!photo.naturalWidth || !photo.naturalHeight || photo.naturalWidth * photo.naturalHeight > 80000000) throw new Error('Invalid image dimensions');
        this.photo = photo;
        this.resetCrop(); this.preparePhoto();
        this.querySelector('[data-charm-file-label]').textContent = this.messages.replace;
        this.querySelector('[data-charm-photo-status]').textContent = `${file.name} · ${this.messages.photo_ready}`;
        this.querySelector('[data-charm-editor]').hidden = false;
        this.querySelector('[data-charm-show-preview]').hidden = false;
        this.render(); this.showPreview(true);
      } catch {
        if (revision === this.photoRevision) {
          this.photoInput.value = ''; this.photo = null; this.showError(this.messages.file_error);
        }
      } finally {
        URL.revokeObjectURL(url);
        if (revision === this.photoRevision) { this.loadingPhoto = false; this.updateVariant(false); }
      }
    }

    preparePhoto() {
      if (!this.photo) return;
      const factor = Math.min(1, 3000 / Math.max(this.photo.naturalWidth, this.photo.naturalHeight));
      const width = Math.round(this.photo.naturalWidth * factor), height = Math.round(this.photo.naturalHeight * factor);
      const rotated = this.crop.rotation % 180 !== 0;
      const canvas = document.createElement('canvas');
      canvas.width = rotated ? height : width; canvas.height = rotated ? width : height;
      const context = canvas.getContext('2d');
      context.translate(canvas.width / 2, canvas.height / 2); context.rotate(this.crop.rotation * Math.PI / 180);
      context.drawImage(this.photo, -width / 2, -height / 2, width, height);
      this.rotatedPhoto = canvas;
    }

    drawPhoto(context, x, y, size, circle = true) {
      if (!this.rotatedPhoto) return;
      const image = this.rotatedPhoto;
      const scale = Math.max(size / image.width, size / image.height) * this.crop.zoom;
      const width = image.width * scale, height = image.height * scale;
      const left = x + (size - width) / 2 + (width - size) / 2 * this.crop.horizontal / 100;
      const top = y + (size - height) / 2 + (height - size) / 2 * this.crop.vertical / 100;
      context.save(); context.beginPath();
      if (circle) context.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
      else context.rect(x, y, size, size);
      context.clip(); context.fillStyle = '#fff'; context.fillRect(x, y, size, size);
      context.drawImage(image, left, top, width, height); context.restore();
    }

    drawText(context) {
      const value = this.textInput.value.trim();
      if (!value) return;
      let size = 34.8;
      context.font = `600 ${size}px "Echoes Serif", Georgia, serif`;
      while (context.measureText(value).width > 365 && size > 12) { size -= .5; context.font = `600 ${size}px "Echoes Serif", Georgia, serif`; }
      const characters = [...value], widths = characters.map(letter => context.measureText(letter).width);
      const radius = 214.34, total = widths.reduce((sum, width) => sum + width, 0);
      let position = -total / 2;
      context.fillStyle = '#101010'; context.textAlign = 'center'; context.textBaseline = 'alphabetic';
      characters.forEach((letter, index) => {
        const angle = Math.PI / 2 - (position + widths[index] / 2) / radius;
        context.save(); context.translate(505.16 + radius * Math.cos(angle), 695.6 + radius * Math.sin(angle));
        context.rotate(angle - Math.PI / 2); context.fillText(letter, 0, 0); context.restore();
        position += widths[index];
      });
    }

    drawDesign(context) {
      context.drawImage(this.assets.base, 277.91, 483.82, 448.02, 448.02);
      const quote = this.querySelector('[data-charm-quote]:checked').dataset.charmQuote;
      context.drawImage(this.assets[`quote${quote}`], 255.605, 438.615, 493.21, 493.21);
      context.drawImage(this.assets.border, 255.605, 438.615, 493.21, 493.21);
      this.drawPhoto(context, 339.815, 542.085, 326.07);
      this.drawText(context);
    }

    render() {
      if (!this.ready || !this.photo) return;
      const scene = this.scene.getContext('2d'); scene.clearRect(0, 0, 1000, 1000);
      this.drawDesign(scene); scene.drawImage(this.assets.scene, 0, 0, 1000, 1000);
      const detail = this.detail.getContext('2d'); detail.clearRect(0, 0, 600, 600);
      detail.save(); detail.scale(600 / 520, 600 / 520); detail.translate(-245, -438); this.drawDesign(detail); detail.restore();
    }

    showPreview(show) {
      const visible = show && !!this.photo;
      this.scene.hidden = !visible;
      this.querySelector('.photo-charm-gallery-image').hidden = visible;
      this.querySelector('[data-charm-badge]').hidden = !visible;
      this.querySelector('[data-charm-show-preview]').setAttribute('aria-pressed', String(visible));
      if (visible) this.querySelectorAll('[data-charm-gallery]').forEach(button => button.setAttribute('aria-pressed', 'false'));
    }

    changed() {
      this.review.checked = false; this.clearError(); this.status.textContent = '';
      this.render(); if (this.photo) this.showPreview(true);
    }

    clearError() { this.error.hidden = true; this.error.textContent = ''; }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }

    toBlob(canvas) {
      return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Image export failed')), 'image/png'));
    }

    async add(event) {
      event.preventDefault();
      if (this.busy || !this.ready || this.loadingPhoto || !this.available) return;
      this.textInput.setCustomValidity(this.textInput.value.trim() ? '' : this.messages.text_error);
      if (!this.form.reportValidity() || !this.photo) return;
      this.busy = true; this.clearError(); this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = this.messages.saving; this.updateVariant(false);
      const data = new FormData(this.form);
      data.set('properties[Personalized text]', this.textInput.value.trim());
      data.set('properties[_Photo placement]', JSON.stringify({ version: 'photo-charm-v1', ...this.crop }));
      const locked = [...this.form.querySelectorAll('input, button')].filter(element => !element.disabled);
      locked.forEach(element => element.disabled = true);
      let requestSent = false;
      try {
        this.render();
        const crop = document.createElement('canvas'); crop.width = 1400; crop.height = 1400;
        this.drawPhoto(crop.getContext('2d'), 0, 0, 1400, false);
        const [preview, cropped] = await Promise.all([this.toBlob(this.scene), this.toBlob(crop)]);
        data.set('properties[Design preview]', preview, 'pet-charm-design.png');
        data.set('properties[Photo crop]', cropped, 'pet-charm-photo-crop.png');
        data.delete('form_type'); data.delete('utf8');
        requestSent = true;
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        if (!response.ok) {
          requestSent = false;
          const result = await response.json().catch(() => ({}));
          throw new Error(typeof result.description === 'string' ? result.description : this.messages.save_error);
        }
        await response.json();
        this.status.textContent = this.messages.added;
        document.dispatchEvent(new CustomEvent('echoes:cart-refresh', { detail: { open: true } }));
      } catch (error) {
        this.status.textContent = '';
        this.showError(requestSent ? this.messages.save_error : (error.message || this.messages.save_error));
      } finally {
        locked.forEach(element => element.disabled = false);
        this.busy = false; this.form.removeAttribute('aria-busy'); this.updateVariant(false);
      }
    }
  });
}
